import React, { useEffect, useState } from "react";
import api from "../../api/axios";
import BarChart from "../ui/BarChart";
import ModelSelect from "../ui/ModelSelect";

const MargeNette = () => {
  const [couteTransport, setCouteTransport] = useState(0);
  const [selectedModel, setSelectedModel] = useState("");
  const [models, setModels] = useState([]);
  const [error, setError] = useState(null);
  const [systemConstant, setSystemConstant] = useState({
    Nombre_d_heures_par_jour: "",
    Taux_horaire_SMIG_16_29: "",
    Taux_horaire_17_00: "",
    Taux_horaire_17_50: "",
    Masse_salariale_16_29: "",
    Masse_salariale_17_00: "",
    Masse_salariale_17_50: "",
    Capacité_par_unité_transport: "",
    cotisation_entroprise_trans: "",
    Coût_par_trajet: "",
    Coût_énergie_journalier: "",
    Coût_charges_fixes_journalier: "",
  });

  const [effectifDirect, setEffectifDirect] = useState(0);
  const [effectifInDirect, setEffectifInDirect] = useState(0);
  const [totalChains, setTotalChains] = useState(0);
  const [totalHours, setTotalHours] = useState(0);
  const [selectedChain, setSelectedChain] = useState("");
  const [chains, setChains] = useState([]);
  const [massSalaryDirect, setMassSalaryDirect] = useState(0);
  const [massSalaryInDirect, setMassSalaryInDirect] = useState(0);
  const [totalMassSalary, setTotalMassSalary] = useState(0);
  const [totalExport, setTotalExport] = useState(0);
  const [coûtEnergie, setCoûtEnergie] = useState(0);
  const [coûtChargesFixes, setCoûtChargesFixes] = useState(0);
  const [coûtDeRevient, setCoûtDeRevient] = useState(0);
  const [productionDepotValeur, setProductionDepotValeur] = useState(0);
  const [margeBruteDepot, setMargeBruteDepot] = useState(0);
  const [margeNetteDepot, setMargeNetteDepot] = useState(0);

  // Add missing fil/plastic state variables
  const [filsParChain, setFilsParChain] = useState(0);
  const [plastiqueParChain, setPlastiqueParChain] = useState(0);
  const [filsParChainPre, setFilsParChainPre] = useState(0);
  const [plastiqueParChainPre, setPlastiqueParChainPre] = useState(0);
  const [otherCostParChain, setOtherCostParChain] = useState(0);
  const [otherCostParChainPre, setOtherCostParChainPre] = useState(0);
  const [hasSystemConstant, setHasSystemConstant] = useState(false);

  // Add state for the three values effective indirect data by date
  const [effectiveIndirectThreeValuesByDate, setEffectiveIndirectThreeValuesByDate] = useState({});
  const [effectifInDirectDepot, setEffectifInDirectDepot] = useState(0);
  const [plastiqueDepot, setPlastiqueDepot] = useState(0);
  const [transportCostEffectiveIndirectDepot, setTransportCostEffectiveIndirectDepot] = useState(0);
  const [totalCostDepot, setTotalCostDepot] = useState(0);
  const [barChartData, setBarChartData] = useState({
    labels: [
      "Production en valeur",
      "Coût de revient",
      "Marge nette",
    ],
    datasets: [
      {
        data: [0, 0, 0, 0],
        backgroundColor: ["rgba(75,192,192,0.2)"],
        borderColor: ["rgba(75,192,192,1)"],
        borderWidth: 1,
      },
    ],
  });

  const barChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
        display: false,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "",
        },
      },
      y: {
        title: {
          display: true,
          text: "Value",
        },
      },
    },
  };

  // ===== COMPREHENSIVE FUNCTION: Calculate Marge Nette for a Chain =====
  // This function takes only a chain ID and returns the three main values:
  // 1. Production en valeur (Production Value)
  // 2. Coût de revient (Cost of Production)
  // 3. Marge nette (Net Margin)
  const calculateMargeNetteForChain = async (chainId) => {
    console.log("🚀 === STARTING COMPREHENSIVE CALCULATION FOR CHAIN:", chainId, "===");


    try {

      // Step 1: Get model ID from chain
      const modelResponse = await api.get(`/getmodelsByChain/${chainId}`);
      const modelId = modelResponse.data.id;
      console.log("📋 Step 1 - Model ID:", modelId);

     // Fetch the three values effective indirect data by day
     const fetchEffectiveIndirectThreeValues = async (modelId, chainId) => {
       try {
         const response = await api.get(`/effective_indirect_by_model_chain_day_three_values/${modelId}/${chainId}`);
         setEffectiveIndirectThreeValuesByDate(response.data);
         console.log("Three values effective indirect data by date:", response.data);
         return response.data; // Return the data so we can use it immediately
       } catch (error) {
         console.error("Error fetching effective indirect three values:", error);
         setError(error.response?.data?.message || "An error occurred");
         return {}; // Return empty object on error
       }
     };

       // Wait for the data to be fetched
       const effectiveIndirectThreeValuesData = await fetchEffectiveIndirectThreeValues(modelId, chainId);


      // Step 2: Get all required data in parallel
      const [
        modelsResponse,
        systemResponse,
        chainDataResponse,
        cleResponse,
        workedHoursResponse,
        effectiveDirectResponse,
        effectiveIndirectResponse,
        filPlasticResponse,
        totalChainsResponse
      ] = await Promise.all([
        api.get("/models"),
        api.get("/system_constants_latest"),
        api.get(`/chain_production_data/${modelId}`, { params: { chain: chainId } }),
        api.get(`/product_plans_cle/${chainId}`),
        api.get(`/production_real_hours/${modelId}/${chainId}`),
        api.get(`/effective_real_total/${modelId}/${chainId}`),
        api.get(`/effective_indirect_by_model_chain_day/${modelId}/${chainId}`),
        api.get(`/plastique_fil_per_chain/${modelId}`).catch(() => ({ data: { hasRealData: false } })),
        api.get(`/total_chain`)
      ]);

      // Extract data
      const models = modelsResponse.data;
      const systemConstants = systemResponse.data;
      const chainData = chainDataResponse.data[chainId];
      const cle = cleResponse.data.cle;
      const workedHoursByDate = workedHoursResponse.data.worked_hours_by_date || {};
      const effectiveDataByDate = effectiveDirectResponse.data;
      const effectiveIndirectDataByDate = effectiveIndirectResponse.data;
      const filPlasticData = filPlasticResponse.data;
      const totalChains = parseInt(totalChainsResponse.data.total_chains);

          // Day-by-day calculation for the three values effective indirect (same logic as MargeNetteChainChart.js)
    const calculateDayByDayEffectifIndirectsThreeValues = (effectiveIndirectData, workedHoursData) => {
      let total = 0;

      console.log("🔍 === DAY-BY-DAY CALCULATION (THREE VALUES INDIRECT) ===");
      console.log("📊 Effective Indirect Three Values Data:", effectiveIndirectData);
      console.log("📊 Worked Hours Data:", workedHoursData);

      // Loop through all dates in the effective indirect data
      Object.keys(effectiveIndirectData).forEach(date => {
        const dayData = effectiveIndirectData[date];
        const workedHours = workedHoursData[date] || 0;

        if (dayData && dayData.effectifIndirects) {
          // Use the same calculation logic as MargeNetteChainChart.js
          let dayCalculation = dayData.effectifIndirects * cle;
          dayCalculation = dayCalculation * workedHours;
          total += dayCalculation;

          console.log("dayCalculation", dayCalculation);
          console.log("dayData.effectifIndirects", dayData.effectifIndirects);
          console.log("cle", cle);
          console.log("workedHours", workedHours);


          console.log(`📅 ${date}:`, {
            effectifIndirects: dayData.effectifIndirects,
            workedHours: workedHours,
            calculation: `${dayData.effectifIndirects} × ${workedHours} = ${dayCalculation}`,
            runningTotal: total
          });
        }
      });

      console.log("🧮 Final Total (Day-by-Day Three Values Indirect):", total);
      console.log("🔍 === END DAY-BY-DAY CALCULATION (THREE VALUES INDIRECT) ===");

      return total;
    };


      // Find model data
      const modelData = models.find(model => model.id == modelId);
      if (!modelData) {
        throw new Error(`Model not found for ID: ${modelId}`);
      }

      console.log("📊 Step 2 - Data loaded:", {
        modelData: modelData.modele,
        cle,
        totalChains,
        systemConstants: Object.keys(systemConstants).length,
        workedHoursDates: Object.keys(workedHoursByDate).length,
        effectiveDirectDates: Object.keys(effectiveDataByDate).length,
        effectiveIndirectDates: Object.keys(effectiveIndirectDataByDate).length
      });

      // Step 3: Calculate Production Value
      const totalExport = chainData.latestSortie || 0;
      const productionValue = (
        modelData.prixMOver *
        (modelData.prixFacture / (modelData.prixMOver * modelData.qte_societe)) *
        totalExport
      ).toFixed(2);

      console.log("💰 Step 3 - Production Value:", {
        totalExport,
        prixMOver: modelData.prixMOver,
        prixFacture: modelData.prixFacture,
        qte_societe: modelData.qte_societe,
        productionValue
      });

      // Step 4: Calculate day-by-day effective direct and indirect
      let totalEffectifDirect = 0;
      let totalEffectifIndirect = 0;
      let totalWorkedHours = 0;

      Object.keys(workedHoursByDate).forEach(date => {
        const workedHours = workedHoursByDate[date] || 0;
        const directData = effectiveDataByDate[date];
        const indirectData = effectiveIndirectDataByDate[date];

        if (workedHours > 0) {
          totalWorkedHours += workedHours;

          if (directData && directData.effectifDirects) {
            totalEffectifDirect += directData.effectifDirects * workedHours;
          }

          if (indirectData && indirectData.effectifIndirects) {
            totalEffectifIndirect += indirectData.effectifIndirects * cle * workedHours;
          }
        }
      });

       console.log("effectiveIndirectThreeValuesData", effectiveIndirectThreeValuesData);
       console.log("totalWorkedHours", totalWorkedHours);
       console.log("Object.keys(effectiveIndirectThreeValuesData).length", Object.keys(effectiveIndirectThreeValuesData).length);
       
       let dayByDayThreeValuesTotal = 0;
       if (Object.keys(effectiveIndirectThreeValuesData).length > 0 && totalWorkedHours > 0) {
         dayByDayThreeValuesTotal = calculateDayByDayEffectifIndirectsThreeValues(effectiveIndirectThreeValuesData, workedHoursByDate);
         setEffectifInDirectDepot(dayByDayThreeValuesTotal);
         console.log("🔄 Recalculated three values effective indirect with day-by-day calculation:", dayByDayThreeValuesTotal);
       }

      // Step 6: Calculate Transport Cost (day-by-day)
      const {
        Capacité_par_unité_transport,
        Coût_par_trajet,
        cotisation_entroprise_trans
      } = systemConstants;

      let totalTransportCostEffectiveIndirect = 0;
      Object.keys(workedHoursByDate).forEach(date => {
        const workedHours = workedHoursByDate[date] || 0;
        const indirectData = effectiveIndirectThreeValuesData[date];

        console.log("indirectData", indirectData);

        if (workedHours > 0 && (indirectData)) {
          const effectifIndirectDay = indirectData ? indirectData.effectifIndirects : 0;
          const QP_effectifDay = effectifIndirectDay * cle;
          const Nombre_de_jours_de_production_day = 1;

          const Refacturation_transport_MOD_indirecte = -(
            (cotisation_entroprise_trans * Nombre_de_jours_de_production_day * QP_effectifDay) / 26
          );

          const Nombre_unite_utilise = (QP_effectifDay) / Capacité_par_unité_transport;
          const dailyTransportCost = 2 * Nombre_unite_utilise * Nombre_de_jours_de_production_day * Coût_par_trajet +
            ( Refacturation_transport_MOD_indirecte);

          totalTransportCostEffectiveIndirect += dailyTransportCost;
        }
      });

      console.log("🚚 Step depot - Transport Cost Effective Indirect:", totalTransportCostEffectiveIndirect);
      setTransportCostEffectiveIndirectDepot(totalTransportCostEffectiveIndirect);



      const {
        Taux_horaire_SMIG_16_29,
        Taux_horaire_17_00,
        Taux_horaire_17_50,
        Masse_salariale_16_29,
        Masse_salariale_17_00,
        Masse_salariale_17_50,
        Nombre_d_heures_par_jour
      } = systemConstants;

       const QP_effectifDepot = dayByDayThreeValuesTotal;
       console.log("QP_effectifDepot", QP_effectifDepot);

      const congeAnualIndirectDepot =
        ( QP_effectifDepot * Taux_horaire_SMIG_16_29 * 1.5) / 26;
      console.log("congeAnualIndirectDepot", congeAnualIndirectDepot);
  
      const masseSalarialeIndirect1629Depot =
        QP_effectifDepot *
        Masse_salariale_16_29 *
        0.01 *
        Taux_horaire_SMIG_16_29;
      console.log("masseSalarialeIndirect1629Depot", masseSalarialeIndirect1629Depot);
  
      const masseSalarialeIndirect1700Depot =
        QP_effectifDepot *
        Masse_salariale_17_00 *
        0.01 *
        Taux_horaire_17_00;
  
      const masseSalarialeIndirect1750Depot =
        QP_effectifDepot *
        Masse_salariale_17_50 *
        0.01 *
        Taux_horaire_17_50;

        const masseSalarialeInDirecteDepot =
        masseSalarialeIndirect1700Depot +
        masseSalarialeIndirect1629Depot +
        masseSalarialeIndirect1750Depot +
        congeAnualIndirectDepot;

      console.log("💵 Step depot - Mass Salary:", {
        masseSalarialeInDirecteDepot,
        masseSalarialeIndirect1700Depot,
        masseSalarialeIndirect1629Depot,
        masseSalarialeIndirect1750Depot,
        congeAnualIndirectDepot
      });
  
  

      const totalCostDepot =  totalTransportCostEffectiveIndirect + masseSalarialeInDirecteDepot;
      setTotalCostDepot(totalCostDepot);

      console.log("🔍 Step depot - Total Cost:", totalCostDepot);



      console.log("👥 Step 4 - Effective Calculations:", {
        totalWorkedHours,
        totalEffectifDirect,
        totalEffectifIndirect,
        cle
      });

      // Step 5: Calculate Mass Salary
   

      const QP_effectif = totalEffectifIndirect;
      const Nombre_de_jours_de_production = totalWorkedHours / Nombre_d_heures_par_jour;

      // Direct salary calculations
      const congeAnual = (totalEffectifDirect * Taux_horaire_SMIG_16_29 * 1.5) / 26;
      const masseSalariale1629 = totalEffectifDirect * Masse_salariale_16_29 * 0.01 * Taux_horaire_SMIG_16_29;
      const masseSalariale1700 = totalEffectifDirect * Masse_salariale_17_00 * 0.01 * Taux_horaire_17_00;
      const masseSalariale1750 = totalEffectifDirect * Masse_salariale_17_50 * 0.01 * Taux_horaire_17_50;

      // Indirect salary calculations
      const congeAnualIndirect = (QP_effectif * Taux_horaire_SMIG_16_29 * 1.5) / 26;
      const masseSalarialeIndirect1629 = QP_effectif * Masse_salariale_16_29 * 0.01 * Taux_horaire_SMIG_16_29;
      const masseSalarialeIndirect1700 = QP_effectif * Masse_salariale_17_00 * 0.01 * Taux_horaire_17_00;
      const masseSalarialeIndirect1750 = QP_effectif * Masse_salariale_17_50 * 0.01 * Taux_horaire_17_50;

      const masseSalarialeDirecte = masseSalariale1629 + masseSalariale1700 + masseSalariale1750 + congeAnual;
      const masseSalarialeInDirecte = masseSalarialeIndirect1700 + masseSalarialeIndirect1629 + masseSalarialeIndirect1750 + congeAnualIndirect;
      const totalMassSalary = masseSalarialeDirecte + masseSalarialeInDirecte;

      console.log("💵 Step 5 - Mass Salary:", {
        masseSalarialeDirecte,
        masseSalarialeInDirecte,
        totalMassSalary
      });

      // Step 6: Calculate Transport Cost (day-by-day)

      let totalTransportCost = 0;
      Object.keys(workedHoursByDate).forEach(date => {
        const workedHours = workedHoursByDate[date] || 0;
        const directData = effectiveDataByDate[date];
        const indirectData = effectiveIndirectDataByDate[date];

        if (workedHours > 0 && (directData || indirectData)) {
          const effectifDirectDay = directData ? directData.effectifDirects : 0;
          const effectifIndirectDay = indirectData ? indirectData.effectifIndirects : 0;
          const QP_effectifDay = effectifIndirectDay * cle;
          const Nombre_de_jours_de_production_day = 1;

          const Refacturation_transport_MOD_indirecte = -(
            (cotisation_entroprise_trans * Nombre_de_jours_de_production_day * QP_effectifDay) / 26
          );
          const Refacturation_transport_MOD_directe = -(
            (cotisation_entroprise_trans * Nombre_de_jours_de_production_day * effectifDirectDay) / 26
          );

          const Nombre_unite_utilise = (QP_effectifDay + effectifDirectDay) / Capacité_par_unité_transport;
          const dailyTransportCost = 2 * Nombre_unite_utilise * Nombre_de_jours_de_production_day * Coût_par_trajet +
            (Refacturation_transport_MOD_directe + Refacturation_transport_MOD_indirecte);

          totalTransportCost += dailyTransportCost;
        }
      });

      console.log("🚚 Step 6 - Transport Cost:", totalTransportCost);

      // Step 7: Calculate Energy and Charges Fixes Costs
      const { Coût_énergie_journalier, Coût_charges_fixes_journalier } = systemConstants;

      let totalEnergyCost = 0;
      let totalChargesFixesCost = 0;

      Object.keys(workedHoursByDate).forEach(date => {
        const workedHours = workedHoursByDate[date] || 0;
        if (workedHours > 0) {
          const Nombre_de_jours_de_production_day = workedHours / Nombre_d_heures_par_jour;
          totalEnergyCost += Nombre_de_jours_de_production_day * Coût_énergie_journalier * cle;
          totalChargesFixesCost += Nombre_de_jours_de_production_day * Coût_charges_fixes_journalier * cle;
        }
      });

      console.log("⚡ Step 7 - Energy & Charges Fixes:", {
        totalEnergyCost,
        totalChargesFixesCost
      });

      // Step 8: Calculate Fil/Plastic Costs
      const baseReference = Number(systemConstant.Base_reference_matiere_fil) || 20000;
      const filUnitCost = Number(systemConstant.Coût_fil_unitaire) || 0;
      const filCost = totalExport * (filUnitCost / baseReference);

      setFilsParChain(filCost);

      console.log("🧵 Step 8 - Fil/Plastic Cost:", filCost);

      // Step 9: Calculate Total Cost
      const totalCost = totalMassSalary + totalTransportCost + totalChargesFixesCost + totalEnergyCost + filCost;

      console.log("🧮 Step 9 - Total Cost Breakdown:", {
        totalMassSalary,
        totalTransportCost,
        totalChargesFixesCost,
        totalEnergyCost,
        filCost,
        totalCost
      });

      // Step 10: Calculate Margins
      const margeBrute = parseFloat(productionValue) - totalCost;
      const margeNette = margeBrute > 0 ? margeBrute - margeBrute * 0.2 : margeBrute;

      console.log("📈 Step 10 - Margins:", {
        productionValue: parseFloat(productionValue),
        totalCost,
        margeBrute,
        margeNette
      });

      const result = {
        productionValue: parseFloat(productionValue),
        costOfProduction: parseFloat(totalCost.toFixed(2)),
        netMargin: parseFloat(margeNette.toFixed(2)),
        totalCostDepot: parseFloat(totalCostDepot.toFixed(2)),
        details: {
          chainId,
          modelId,
          modelName: modelData.modele,
          totalExport,
          totalWorkedHours,
          totalEffectifDirect,
          totalEffectifIndirect,
          cle,
          totalChains,
          massSalary: {
            direct: parseFloat(masseSalarialeDirecte.toFixed(2)),
            indirect: parseFloat(masseSalarialeInDirecte.toFixed(2)),
            total: parseFloat(totalMassSalary.toFixed(2))
          },
          transportCost: parseFloat(totalTransportCost.toFixed(2)),
          energyCost: parseFloat(totalEnergyCost.toFixed(2)),
          chargesFixesCost: parseFloat(totalChargesFixesCost.toFixed(2)),
          filCost: parseFloat(filCost.toFixed(2)),
          margeBrute: parseFloat(margeBrute.toFixed(2))
        }
      };

      console.log("✅ === COMPREHENSIVE CALCULATION COMPLETED ===");
      console.log("🎯 Final Result:", result);

      return result;

    } catch (error) {
      console.error("❌ Error in calculateMargeNetteForChain:", error);
      throw new Error(`Failed to calculate marge nette for chain ${chainId}: ${error.message}`);
    }
  };

  const getMargeNetteTotalForModel = async (Selectedmodel) => {
    console.log("🏭 === STARTING TOTAL CALCULATION FOR MODEL:", Selectedmodel, "===");

    try {
      const chains = await api.get(`/chains`);
      const chainsData = chains.data;

      // Variables to accumulate totals
      let totalProductionValue = 0;
      let totalCostOfProduction = 0;
      let totalCostDepot = 0;
      let plastiqueValue = 0;

      // Get plastique value once for the model
      const response = await api.get(`/magasin_production/${Selectedmodel}`);
      const baseReference = Number(systemConstant.Base_reference_matiere_plastique) || 15000;
      const plasticUnitCost = Number(systemConstant.Coût_plastique_unitaire) || 0;
      plastiqueValue = response.data.value * (plasticUnitCost / baseReference);
      setPlastiqueDepot(plastiqueValue);
      console.log("🧵 Plastique Value for model:", plastiqueValue);

      // Process each chain
      for (const chain of chainsData) {
        try {
          const modelResponse = await api.get(`/getmodelsByChain/${chain.id}`);
          const modelId = modelResponse.data.id;
          
          if (modelId == Selectedmodel) {
            console.log(`🔄 Processing chain ${chain.id} (${chain.name})...`);
            const result = await calculateMargeNetteForChain(chain.id);
            
            // Accumulate the values
            totalProductionValue += result.productionValue;
            totalCostOfProduction += result.costOfProduction;
            totalCostDepot += result.totalCostDepot;
            
            console.log(`✅ Chain ${chain.id} completed:`, {
              productionValue: result.productionValue,
              costOfProduction: result.costOfProduction,
              totalCostDepot: result.totalCostDepot
            });
          }
        } catch (error) {
          console.error(`❌ Error processing chain ${chain.id}:`, error);
          // Continue with other chains
        }
      }

      // Calculate final totals
      const modelsResponse = await api.get("/models");
      const modelData = modelsResponse.data.find((model) => {
          return model.id == Selectedmodel;
        });

      const productionChainValeur = (
        modelData.prixMOver *
        (modelData.prixFacture /
          (modelData.prixMOver * modelData.qte_societe)) *
        (response.data.value || 0)
      ).toFixed(2);

      const finalCostOfProduction = totalCostOfProduction + totalCostDepot + plastiqueValue;
      const finalNetMargin = productionChainValeur - finalCostOfProduction;

      console.log("🎯 === FINAL CALCULATIONS ===");
      console.log("📊 Accumulated Values:", {
        productionChainValeur,
        totalCostOfProduction,
        totalCostDepot,
        plastiqueValue,
        finalCostOfProduction,
        finalNetMargin
      });

      // Update the chart data
      const dataValues = [
        parseFloat(Number(productionChainValeur).toFixed(2)),
        parseFloat(finalCostOfProduction.toFixed(2)),
        parseFloat(finalNetMargin.toFixed(2)),
      ];

      const backgroundColors = dataValues.map((value) =>
        value < 0 ? "rgba(255,0,0,0.2)" : "rgba(75,192,192,0.2)"
      );

      const borderColors = dataValues.map((value) =>
        value < 0 ? "rgba(255,0,0,1)" : "rgba(75,192,192,1)"
      );

      setBarChartData({
        labels: ["Production en valeur", "Coût de revient", "Marge nette"],
        datasets: [{
          data: dataValues,
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: 1,
        }],
      });

      console.log("✅ === CHART UPDATED WITH FINAL VALUES ===");
      console.log("📈 Final Chart Values:", {
        productionValue: dataValues[0],
        costOfProduction: dataValues[1],
        netMargin: dataValues[2]
      });

    } catch (error) {
      console.error("❌ Error in getMargeNetteTotalForModel:", error);
      setError(`Failed to calculate total marge nette for model ${Selectedmodel}: ${error.message}`);
    }
  };

  useEffect(() => {
    if (selectedModel) {
      getMargeNetteTotalForModel(selectedModel);
    }
  }, [selectedModel]);


  // Add fil/plastic calculations like in MargeNetteTable.js
  const calculateCouteFilAndPlastique = async () => {
    try {
      if (!selectedModel) {
        console.log("No selectedModel, skipping fil/plastic calculation");
        return;
      }

      if (!totalExport || totalExport <= 0) {
        console.log("No totalExport available, skipping fil/plastic calculation");
        return;
      }

      console.log("🔍 === CALCULATION: Fil et Plastique TOTAL pour le modèle ===");
      console.log("📊 Input Data:");
      console.log("  - selectedModel:", selectedModel);
      console.log("  - totalExport:", totalExport);

      const response = await api.get(`/plastique_fil_per_chain/${selectedModel}`);
      const data = response.data;

      console.log("📡 API Response:", data);
      console.log("📊 Response Details:");
      console.log("  - hasRealData:", data.hasRealData);
      console.log("  - totals:", data.totals);

      if (data.hasRealData && data.totals) {
        // Use TOTAL costs for the whole model (not per chain)
        const totalFilCost = data.totals.filReal || 0;
        const totalPlastiqueCost = data.totals.plastiqueReal || 0;
        const totalOtherCost = data.totals.otherReal || 0;

        // Set the total costs (not per chain)
        setFilsParChain(totalFilCost);
        setPlastiqueParChain(totalPlastiqueCost);
        setOtherCostParChain(totalOtherCost);
        setFilsParChainPre(totalFilCost);
        setPlastiqueParChainPre(totalPlastiqueCost);
        setOtherCostParChainPre(totalOtherCost);

        console.log("✅ Using TOTAL fil/plastic costs for the whole model:", {
          totalFilCost,
          totalPlastiqueCost,
          totalOtherCost,
          totalCombined: totalFilCost + totalPlastiqueCost + totalOtherCost
        });
      } else {
        // Fallback to direct material constants for the whole model
        const baseReferenceFil = Number(systemConstant.Base_reference_matiere_fil) || 20000;
        const baseReferencePlastique = Number(systemConstant.Base_reference_matiere_plastique) || 15000;
        const filUnitCost = Number(systemConstant.Coût_fil_unitaire) || 0;
        const plasticUnitCost = Number(systemConstant.Coût_plastique_unitaire) || 0;
        const filCost = totalExport * (filUnitCost / baseReferenceFil);
        const plastiqueCost = totalExport * (plasticUnitCost / baseReferencePlastique);
        const otherCost = 0;

        setFilsParChain(filCost);
        setPlastiqueParChain(plastiqueCost);
        setOtherCostParChain(otherCost);
        setFilsParChainPre(filCost);
        setPlastiqueParChainPre(plastiqueCost);
        setOtherCostParChainPre(otherCost);

        console.log("⚠️ Using fallback calculation (same as MargeNetteTable.js):", {
          totalExport,
          filCost,
          plastiqueCost,
          otherCost,
          totalCombined: filCost + plastiqueCost + otherCost
        });
      }
    } catch (error) {
      console.error("❌ Error fetching fil/plastic data:", error);
      // Fallback values for the whole model using the same formula
      if (totalExport && totalExport > 0) {
        const baseReferenceFil = Number(systemConstant.Base_reference_matiere_fil) || 20000;
        const baseReferencePlastique = Number(systemConstant.Base_reference_matiere_plastique) || 15000;
        const filUnitCost = Number(systemConstant.Coût_fil_unitaire) || 0;
        const plasticUnitCost = Number(systemConstant.Coût_plastique_unitaire) || 0;
        const filCost = totalExport * (filUnitCost / baseReferenceFil);
        const plastiqueCost = totalExport * (plasticUnitCost / baseReferencePlastique);
        const otherCost = 0;

        setFilsParChain(filCost);
        setPlastiqueParChain(plastiqueCost);
        setOtherCostParChain(otherCost);
        setFilsParChainPre(filCost);
        setPlastiqueParChainPre(plastiqueCost);
        setOtherCostParChainPre(otherCost);

        console.log("💰 Error fallback calculation (same as MargeNetteTable.js):", {
          filCost,
          plastiqueCost,
          otherCost
        });
      } else {
        // If no totalExport, use hardcoded values
        setFilsParChain(200);
        setPlastiqueParChain(800);
        setOtherCostParChain(0);
        setFilsParChainPre(200);
        setPlastiqueParChainPre(800);
        setOtherCostParChainPre(0);
      }
    }
    console.log("🔍 === END CALCULATION ===");
  };

  const fetchChainData = async (modelId, chainId) => {
    try {
      setError(null);
      const response = await api.get(
        `/magasin_production/${modelId}`
      );

      console.log(response.data.value)
      setTotalExport(response.data.value);

      const modelData = models.find((model) => {
        return model.id == modelId;
      });

      const productionDepotValeur = (
        modelData.prixMOver *
        (modelData.prixFacture /
          (modelData.prixMOver * modelData.qte_societe)) *
        response.data.value
      ).toFixed(2);

      setProductionDepotValeur(productionDepotValeur);

      // Fetch fil/plastic data when chain data is fetched
    } catch (error) {
      console.error(error);
      setError(error.response?.data?.message || "An error occurred");
    }
  };

  useEffect(() => {
    if (selectedModel && totalExport && totalExport > 0) {
      calculateCouteFilAndPlastique();
    }
  }, [selectedModel, totalExport]);

  useEffect(() => {
    const fetchChains = async () => {
      try {
        const response = await api.get("/chains");
        setChains(response.data);
      } catch (error) {
        console.error("Error fetching chains:", error);
      }
    };

    fetchChains();
  }, [])

  const calculateCouteTransport = () => {
    const QP_effectif = effectifInDirect / totalChains;
    const {
      Nombre_d_heures_par_jour,
      Capacité_par_unité_transport,
      Coût_par_trajet,
      cotisation_entroprise_trans,
    } = systemConstant;

    const Nombre_de_jours_de_production = totalHours / Nombre_d_heures_par_jour;

    const Refacturation_transport_MOD_indirecte = -(
      (cotisation_entroprise_trans *
        Nombre_de_jours_de_production *
        QP_effectif) /
      26
    );

    const Refacturation_transport_MOD_directe = -(
      (cotisation_entroprise_trans *
        Nombre_de_jours_de_production *
        effectifDirect) /
      26
    );

    const Nombre_unite_utilise =
      (QP_effectif + effectifDirect) / Capacité_par_unité_transport;

    const transportCost =
      2 *
      Nombre_unite_utilise *
      Nombre_de_jours_de_production *
      Coût_par_trajet +
      (Refacturation_transport_MOD_directe +
        Refacturation_transport_MOD_indirecte);

    console.log("Transport cost calculation:", {
      QP_effectif,
      Nombre_de_jours_de_production,
      Refacturation_transport_MOD_indirecte,
      Refacturation_transport_MOD_directe,
      Nombre_unite_utilise,
      transportCost
    });

    setCouteTransport(transportCost.toFixed(4));
  };

  const calculateTotalMasseSalariale = () => {
    const {
      Taux_horaire_SMIG_16_29,
      Taux_horaire_17_00,
      Taux_horaire_17_50,
      Masse_salariale_16_29,
      Masse_salariale_17_00,
      Masse_salariale_17_50,
    } = systemConstant;

    const congeAnual =
      (totalHours * effectifDirect * Taux_horaire_SMIG_16_29 * 1.5) / 26;

    const masseSalariale1629 =
      totalHours *
      effectifDirect *
      Masse_salariale_16_29 *
      0.01 *
      Taux_horaire_SMIG_16_29;

    const masseSalariale1700 =
      totalHours *
      effectifDirect *
      Masse_salariale_17_00 *
      0.01 *
      Taux_horaire_17_00;

    const masseSalariale1750 =
      totalHours *
      effectifDirect *
      Masse_salariale_17_50 *
      0.01 *
      Taux_horaire_17_50;

    const QP_effectif = effectifInDirect / totalChains;

    const congeAnualIndirect =
      (totalHours * QP_effectif * Taux_horaire_SMIG_16_29 * 1.5) / 26;

    const masseSalarialeIndirect1629 =
      totalHours *
      QP_effectif *
      Masse_salariale_16_29 *
      0.01 *
      Taux_horaire_SMIG_16_29;

    const masseSalarialeIndirect1700 =
      totalHours *
      QP_effectif *
      Masse_salariale_17_00 *
      0.01 *
      Taux_horaire_17_00;

    const masseSalarialeIndirect1750 =
      totalHours *
      QP_effectif *
      Masse_salariale_17_50 *
      0.01 *
      Taux_horaire_17_50;

    const masseSalarialeDirecte =
      masseSalariale1629 + masseSalariale1700 + masseSalariale1750 + congeAnual;

    const masseSalarialeInDirecte =
      masseSalarialeIndirect1700 +
      masseSalarialeIndirect1629 +
      masseSalarialeIndirect1750 +
      congeAnualIndirect;

    console.log("Salary calculation:", {
      effectifDirect,
      effectifInDirect,
      totalChains,
      QP_effectif,
      totalHours,
      masseSalarialeDirecte,
      masseSalarialeInDirecte,
      total: masseSalarialeDirecte + masseSalarialeInDirecte
    });

    setMassSalaryInDirect(masseSalarialeInDirecte.toFixed(4));
    setMassSalaryDirect(masseSalarialeDirecte.toFixed(4));
    setTotalMassSalary(
      (masseSalarialeInDirecte + masseSalarialeDirecte).toFixed(4)
    );
  };

  const calculateCoûtEnergie = () => {
    const { Coût_énergie_journalier, Nombre_d_heures_par_jour } =
      systemConstant;
    const Nombre_de_jours_de_production = totalHours / Nombre_d_heures_par_jour;

    const energyCost = Nombre_de_jours_de_production * Coût_énergie_journalier;

    console.log("Energy cost calculation:", {
      totalHours,
      Nombre_d_heures_par_jour,
      Nombre_de_jours_de_production,
      Coût_énergie_journalier,
      energyCost
    });

    setCoûtEnergie(energyCost);
  };

  const calculateCoûtChargesFixes = () => {
    const { Coût_charges_fixes_journalier, Nombre_d_heures_par_jour } =
      systemConstant;
    const Nombre_de_jours_de_production = totalHours / Nombre_d_heures_par_jour;

    const chargesFixesCost = Nombre_de_jours_de_production * Coût_charges_fixes_journalier;

    console.log("Charges fixes calculation:", {
      totalHours,
      Nombre_d_heures_par_jour,
      Nombre_de_jours_de_production,
      Coût_charges_fixes_journalier,
      chargesFixesCost
    });

    setCoûtChargesFixes(chargesFixesCost);
  };

  const calculateCoûtDeRevient = () => {
    // Add fil/plastic costs to the calculation like in MargeNetteTable.js
    const filPlasticCost = filsParChain + plastiqueParChain + otherCostParChain;

    const totalCost =
      parseFloat(totalMassSalary) +
      parseFloat(couteTransport) +
      parseFloat(coûtChargesFixes) +
      parseFloat(coûtEnergie) +
      parseFloat(filPlasticCost);

    console.log("🔍 === CALCULATION: Coût de revient (MargeNette.js) ===");
    console.log("📊 Input Data:");
    console.log("  - totalMassSalary:", parseFloat(totalMassSalary));
    console.log("  - couteTransport:", parseFloat(couteTransport));
    console.log("  - coûtChargesFixes:", parseFloat(coûtChargesFixes));
    console.log("  - coûtEnergie:", parseFloat(coûtEnergie));
    console.log("  - filPlasticCost (filsParChain + plastiqueParChain + otherCostParChain):", parseFloat(filPlasticCost));
    console.log("    - filsParChain:", parseFloat(filsParChain));
    console.log("    - plastiqueParChain:", parseFloat(plastiqueParChain));
    console.log("    - otherCostParChain:", parseFloat(otherCostParChain));
    console.log("🧮 Total Cost:", totalCost);
    console.log("🔍 === END CALCULATION ===");

    setCoûtDeRevient(totalCost.toFixed(2));
    return totalCost;
  };

  const calculateMargeBruteDepot = () => {
    const coûtDeRevient = calculateCoûtDeRevient();
    const margeBrute = parseFloat(productionDepotValeur) - coûtDeRevient;
    setMargeBruteDepot(margeBrute.toFixed(2));
    return margeBrute;
  };

  const calculateMargeNetteDepot = () => {
    const margeBruteDepot = calculateMargeBruteDepot();
    const margeNette = margeBruteDepot - margeBruteDepot * 0.2;
    setMargeNetteDepot(margeNette.toFixed(2));
    return margeNette;
  };

  const fetchWorkedHours = async (modelId) => {
    try {
      const response = await api.get(`/production_real_hours/${modelId}`);
      setTotalHours(response.data.worked_hours_count);
    } catch (error) {
      console.error("Error fetching worked hours:", error);
      setError(error.response?.data?.message || "An error occurred");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const modelsResponse = await api.get("/models");
        setModels(modelsResponse.data);

        const systemResponse = await api.get("/system_constants_latest");
        if (systemResponse.data) {
          const { id, created_at, updated_at, effectif_fix, ...filteredData } =
            systemResponse.data;
          setSystemConstant({
            ...filteredData,
          });
          setHasSystemConstant(true);
        }

        const responseChains = await api.get(`/total_chain`);
        setTotalChains(parseInt(responseChains.data.total_chains));

        if (selectedModel) {
          const responseDirect = await api.get(
            `/effective_direct_real/${selectedModel}`
          );
          setEffectifDirect(parseInt(responseDirect.data.total_effectif_direct));

          // Try to get model-specific effective indirect average first
          try {
            const responseIndirectAverage = await api.get(`/effective_indirect_average/${selectedModel}`);
            if (responseIndirectAverage.data && responseIndirectAverage.data.average_effective_indirect) {
              setEffectifInDirect(parseInt(responseIndirectAverage.data.average_effective_indirect));
              console.log("Using model-specific effective indirect average:", responseIndirectAverage.data.average_effective_indirect);
            } else {
              // Fallback to general effective indirect
              const responseIndirect = await api.get(`/effective_indirect`);
              setEffectifInDirect(parseInt(responseIndirect.data.total_effectif_indirect));
              console.log("Using general effective indirect:", responseIndirect.data.total_effectif_indirect);
            }
          } catch (error) {
            console.log("Model-specific effective indirect not found, using general endpoint");
            // Fallback to general effective indirect
            const responseIndirect = await api.get(`/effective_indirect`);
            setEffectifInDirect(parseInt(responseIndirect.data.total_effectif_indirect));
            console.log("Using general effective indirect:", responseIndirect.data.total_effectif_indirect);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [selectedModel]);

  useEffect(() => {
    if (
      totalHours > 0 &&
      effectifDirect > 0 &&
      effectifInDirect > 0 &&
      totalChains > 0 &&
      systemConstant.Nombre_d_heures_par_jour &&
      hasSystemConstant
    ) {
      console.log("Calculations triggered with data:", {
        totalHours,
        effectifDirect,
        effectifInDirect,
        totalChains,
        hasSystemConstant,
        filsParChain,
        plastiqueParChain
      });

      calculateCouteTransport();
      calculateTotalMasseSalariale();
      calculateCoûtEnergie();
      calculateCoûtChargesFixes();
    } else {
      console.log("Calculations not triggered - missing data:", {
        totalHours: totalHours > 0,
        effectifDirect: effectifDirect > 0,
        effectifInDirect: effectifInDirect > 0,
        totalChains: totalChains > 0,
        hasSystemConstant,
        filsParChain: filsParChain > 0,
        plastiqueParChain: plastiqueParChain > 0
      });
    }
  }, [
    totalHours,
    effectifDirect,
    effectifInDirect,
    totalChains,
    totalExport,
    systemConstant,
    hasSystemConstant,
    filsParChain,
    plastiqueParChain
  ]);

  useEffect(() => {
    if (selectedModel) {
      fetchWorkedHours(selectedModel);
    }
  }, [selectedModel]);

  // useEffect(() => {
  //   const coûtDeRevient = calculateCoûtDeRevient();
  //   const margeBruteDepot = calculateMargeBruteDepot();
  //   const margeNetteDepot = calculateMargeNetteDepot();

  //   const dataValues = [
  //     parseInt(productionDepotValeur),
  //     coûtDeRevient,
  //     margeNetteDepot,
  //   ];

  //   const backgroundColors = dataValues.map((value) =>
  //     value < 0 ? "rgba(255,0,0,0.2)" : "rgba(75,192,192,0.2)"
  //   );

  //   const borderColors = dataValues.map((value) =>
  //     value < 0 ? "rgba(255,0,0,1)" : "rgba(75,192,192,1)"
  //   );

  //   setBarChartData({
  //     labels: [
  //       "Production en valeur",
  //       "Coût de revient",
  //       "Marge nette",
  //     ],
  //     datasets: [
  //       {
  //         data: dataValues,
  //         backgroundColor: backgroundColors,
  //         borderColor: borderColors,
  //         borderWidth: 1,
  //       },
  //     ],
  //   });
  // }, [
  //   totalMassSalary,
  //   couteTransport,
  //   coûtChargesFixes,
  //   coûtEnergie,
  //   productionDepotValeur,
  // ]);



  const handleSelectChange = (e) => {
    setSelectedModel(e.target.value);
    // fetchChainData(e.target.value);
  };

  // const handleChainChange = (e) => {
  //   setSelectedChain(e.target.value);
  // };



  return (
    <div className="ml-7">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">La Marge Nette Réelle Dépôt Final</h2>
        <div className="flex items-center justify-between">
          <div className="ml-7 mb-4 pr-6">
            <ModelSelect className="mt-4" value={selectedModel} onChange={handleSelectChange} models={models} />
          </div>
        </div>
      </div>
      <div className="w-full mr-5">
        {error ? (
          <div className="text-red-500 ml-7">{error}</div>
        ) : (
          <>

            <BarChart data={barChartData} options={barChartOptions} />
          </>
        )}
      </div>
    </div>
  );
};

export default MargeNette;
